<script setup lang="ts">
import { gsap } from 'gsap'

export interface CardGridItem {
  icon?: string
  number?: number
  title: string
  desc: string
}

const props = withDefaults(defineProps<{
  sectionId?: string
  badge?: string
  title: string
  subtitle?: string
  items: CardGridItem[]
  accent: string
  columns?: 2 | 3
  tint?: boolean
}>(), {
  sectionId: undefined,
  badge: undefined,
  subtitle: undefined,
  columns: 3,
})

const section = ref<HTMLElement | null>(null)

onMounted(() => {
  const el = section.value
  if (!el) return

  gsap.fromTo(el.querySelector('.cg-heading'),
    { opacity: 0, y: 40 },
    {
      opacity: 1, y: 0, duration: 0.85, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 80%', once: true },
    },
  )

  gsap.fromTo(el.querySelectorAll('.cg-card'),
    { opacity: 0, y: 34, scale: 0.95 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'back.out(1.4)', stagger: 0.1,
      scrollTrigger: { trigger: el.querySelector('.cg-grid'), start: 'top 85%', once: true },
    },
  )
})
</script>

<template>
  <section :id="sectionId" ref="section" class="py-12 md:py-16 lg:py-20 relative overflow-hidden" :class="tint ? 'bg-amber-50' : 'bg-white'">
    <div v-if="!tint" class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true" />
    <div class="container relative">
      <div class="cg-heading text-center max-w-2xl mx-auto mb-10 md:mb-14">
        <div v-if="badge" class="bfm-badge text-white px-3 py-1.5 md:px-4 md:py-2 text-sm md:text-base mb-4" :style="{ backgroundColor: props.accent }">
          {{ badge }}
        </div>
        <h2 class="text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.25] text-deep-indigo mb-3">{{ title }}</h2>
        <p v-if="subtitle" class="text-base md:text-lg text-overcast-night">{{ subtitle }}</p>
      </div>

      <div
        class="cg-grid grid grid-cols-1 gap-5 md:gap-6"
        :class="columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'">
        <div
          v-for="(item, i) in items"
          :key="i"
          class="cg-card bg-white border-2 border-gray-900 rounded-2xl p-6 md:p-7 shadow-[6px_6px_0_#111827]">
          <div
            class="size-12 md:size-14 rounded-lg border-2 border-gray-900 flex items-center justify-center shadow-[3px_3px_0_#111827] mb-4 md:mb-5 shrink-0 text-white"
            :style="{ backgroundColor: props.accent }">
            <UIcon v-if="item.icon" :name="item.icon" class="size-6 md:size-7 text-white" />
            <span v-else class="text-lg md:text-xl font-black">{{ item.number }}</span>
          </div>
          <h3 class="text-lg md:text-xl font-bold text-gray-900 mb-2 leading-snug">{{ item.title }}</h3>
          <p class="text-sm md:text-base text-overcast-night leading-relaxed">{{ item.desc }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
